import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';

export class MediaPermissionService {
  // Track if permissions are already requested
  private static isInitialized = false;

  // Check if we're on a device that supports native permissions
  static isPlatformSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  // Initialize all media permissions
  static async initializeMediaPermissions(): Promise<void> {
    if (!this.isPlatformSupported()) {
      console.log('⚠️ Media permissions are only supported on native platforms');
      return;
    }

    // Avoid duplicate initialization
    if (this.isInitialized) {
      console.log('ℹ️ Media permissions already initialized');
      return;
    }

    try {
      console.log('🎥 Requesting Camera permission...');
      await this.requestCameraPermission();
      
      console.log('🎤 Requesting Microphone permission...');
      await this.requestMicrophonePermission();
      
      this.isInitialized = true;
      console.log('✅ Media permissions initialization complete');
    } catch (error) {
      console.error('❌ ERROR INITIALIZING MEDIA PERMISSIONS:', error);
    }
  }

  // Request camera permission
  static async requestCameraPermission(): Promise<boolean> {
    if (!this.isPlatformSupported()) {
      return false;
    }

    try {
      // The Camera plugin handles both camera and gallery permissions
      const result = await Camera.requestPermissions({ permissions: ['camera'] });
      console.log('📸 Camera permission result:', result);
      return result.camera === 'granted';
    } catch (error) {
      console.error('❌ ERROR REQUESTING CAMERA PERMISSION:', error);
      return false;
    }
  }

  // Request microphone permission - uses Camera plugin in a special way
  static async requestMicrophonePermission(): Promise<boolean> {
    if (!this.isPlatformSupported()) {
      return false;
    }

    try {
      // Create a temporary HTML audio element to trigger permission dialog
      const audioContext = new AudioContext();
      
      try {
        // This will trigger the permission prompt
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone permission granted');
        
        // If successful, we can close the tracks
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch (err) {
        console.log('❌ Microphone permission denied:', err);
        return false;
      }
    } catch (error) {
      console.error('❌ ERROR REQUESTING MICROPHONE PERMISSION:', error);
      return false;
    }
  }

  // Check if camera permission is granted
  static async checkCameraPermission(): Promise<boolean> {
    if (!this.isPlatformSupported()) {
      return false;
    }

    try {
      const permissionStatus = await Camera.checkPermissions();
      return permissionStatus.camera === 'granted';
    } catch (error) {
      console.error('Error checking camera permission:', error);
      return false;
    }
  }

  // Check if microphone permission is granted - not directly available through Capacitor
  static async checkMicrophonePermission(): Promise<boolean> {
    if (!this.isPlatformSupported()) {
      return false;
    }

    try {
      // Try to access the microphone to check permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Clean up immediately
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch (err) {
        console.log('Microphone permission check failed:', err);
        return false;
      }
    } catch (error) {
      console.error('Error checking microphone permission:', error);
      return false;
    }
  }
}